import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { fetchProductPrices } from '../../utils/api';
import { useTheme } from '../../context/ThemeContext';
import RetailerLogo from '../RetailerLogo';
import { findProductById } from '../../data/productData';
import './SimplePriceComparison.css';
import ProductComparisonHero from './ProductComparisonHero';
import AmazonPriceNotice from './AmazonPriceNotice';
import OfferSavingsBadge from './OfferSavingsBadge';
import { getOfferSavings } from '../../domain/offerSavings';
import {
  formatHeroCurrency,
  computeMsrpSavings,
  getComparableOfferPrice,
  getOfferLink,
  isAmpedOutdoorsRetailer,
  PRICE_GREEN,
  PRICE_GREEN_DARK,
} from './simpleProductPageShared';
import { trackDbPdpOfferClick } from './dbPdpAffiliateClick';
import { AFFILIATE_PAGE_SOURCE } from '../../utils/enhancedAnalytics';
import { usePriceSectionView } from '../../utils/usePriceSectionView';
import {
  getBestVerifiedOffer,
  getBestVerifiedOffers,
  getOfferListPresentation,
  sortVerifiedOffers,
} from './dbPdpPricePage';
import { FaLink, FaQrcode, FaSms, FaFacebookF, FaTwitter } from 'react-icons/fa';
import { QRCodeSVG } from 'qrcode.react';
import "react-toastify/dist/ReactToastify.css";

// CSS is defined inline for simplicity
const SimplePriceComparison = ({
  specificProductId,
  productSlug = null,
  csvLookupId,
  csvExactMatchOnly = true,
  msrp = null,
  isAmpedOutdoors = false,
  prefetchedOffers,
  pricingProductName,
  embeddedInProductPage = false,
  heroProductName = null,
  heroProductImage = null,
  heroFallbackImage = null,
  heroImageOverlay = null,
}) => {
  const { productId: urlProductId } = useParams();
  const productId = specificProductId || urlProductId;
  const { theme } = useTheme();
  
  const isPrefetchedDbOffers = Array.isArray(prefetchedOffers);
  const productDetails = isPrefetchedDbOffers ? null : (
    findProductById('transducer', productId) || 
                        findProductById('screen', productId) || 
                        findProductById('battery', productId) || 
                        findProductById('shuttle', productId) || 
                        findProductById('pole', productId)
  );
  
  const [productData, setProductData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [debugInfo, setDebugInfo] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  
  // Add new states for enhanced loading experience
  const [loadingDuration, setLoadingDuration] = useState(0);
  const [silentRetry, setSilentRetry] = useState(false);
  const loadingTimerRef = useRef(null);
  const retryTimerRef = useRef(null);
  const priceSectionRef = useRef(null);
  
  // Add a new state to track retry attempts
  const [retryAttempts, setRetryAttempts] = useState(0);
  const MAX_RETRY_ATTEMPTS = 3; // Maximum number of retries before showing "No data found"

  // Only render rows that have a valid numeric price (> 0).
  // Prevents null/undefined prices from showing up and avoids NaN savings calculations.
  const filterValidPricedOffers = (offersArr) => {
    if (!Array.isArray(offersArr)) return [];
    return offersArr.filter((item) => {
      const n = item?.numeric_price;
      return typeof n === 'number' && Number.isFinite(n) && n > 0;
    });
  };

  // Check if debug mode is enabled via URL param
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const debug = queryParams.get('debug') === 'true';
    setShowDebug(debug);
    
    if (debug) {
      console.log('Debug mode enabled for PriceComparison');
    }
  }, []);

  // Check if device is mobile or desktop
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setWindowWidth(width);
      const mobile = width <= 768;
      const desktop = width >= 1024;
      
      setIsMobile(mobile);
      setIsDesktop(desktop);

      if (process.env.NODE_ENV === 'development') {
        console.log(`Window width: ${width}px, isMobile: ${mobile}, isDesktop: ${desktop}`);
      }
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    // Prefetched offers from DB-driven path (getOffersByProductId): use directly, skip fetch
    if (Array.isArray(prefetchedOffers)) {
      setProductData(filterValidPricedOffers(prefetchedOffers));
      setError(null);
      setLoading(false);
      setRetryAttempts(0);
      setSilentRetry(false);
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      return;
    }

    const fetchPriceData = async () => {
      try {
        if (!silentRetry) {
          setLoading(true);
        }
        console.log('Fetching price data for product:', productId);
        if (csvLookupId) {
          console.log('Using lookup key for price matching:', csvLookupId);
          if (csvExactMatchOnly) {
            console.log('EXACT MATCH ONLY MODE: Will only return exact matches for this lookup key');
          }
        }
        
        // Track the time for performance debugging
        const startTime = performance.now();
        
        // Pass the csvExactMatchOnly flag to the API
        const result = await fetchProductPrices(
          productId, 
          0, 
          3, 
          csvLookupId, 
          csvExactMatchOnly
        );
        
        // Calculate time taken
        const endTime = performance.now();
        const timeTaken = (endTime - startTime).toFixed(2);
        
        // Collect debug info
        const debugData = {
          timestamp: new Date().toISOString(),
          timeTakenMs: timeTaken,
          productId: productId,
          csvLookupId: csvLookupId || 'Not provided',
          exactMatchOnly: csvExactMatchOnly,
          resultSource: result.source || 'unknown',
          itemCount: result.data?.length || 0,
          usingFallback: result.usingFallback || false,
          browserInfo: {
            userAgent: navigator.userAgent,
            language: navigator.language,
            platform: navigator.platform,
            hostname: window.location.hostname
          }
        };
        
        setDebugInfo(debugData);
        
        if (result.success && result.data.length > 0) {
          // Even if we got data, keep silently retrying if we're using fallback data
          const isUsingFallback = result.usingFallback || false;
          setSilentRetry(isUsingFallback);
          
          setProductData(filterValidPricedOffers(result.data));
          setError(null);
          console.log(`Successfully loaded ${result.data.length} price items in ${timeTaken}ms from source: ${result.source || 'unknown'}`);
          
          // If using fallback data, continue to retry in the background
          if (isUsingFallback) {
            console.log('Using fallback data, will continue to retry for fresh data');
            retryTimerRef.current = setTimeout(() => {
              console.log('Background retry attempt for fresh data');
              fetchPriceData();
            }, 3000); // Retry after 3 seconds
          } else {
            // Clear any retry timers if we have real data
            if (retryTimerRef.current) {
              clearTimeout(retryTimerRef.current);
            }
          }
        } else {
          console.error('No price data found:', result);
          
          // Increment retry attempts
          const newRetryAttempts = retryAttempts + 1;
          setRetryAttempts(newRetryAttempts);
          
          // Only keep retrying silently if we haven't reached the max attempts
          if (newRetryAttempts < MAX_RETRY_ATTEMPTS) {
            // Don't show error to user, just keep retrying silently
            setSilentRetry(true);
            
            // Setup immediate retry for empty data
            retryTimerRef.current = setTimeout(() => {
              console.log(`Retrying after empty data result (attempt ${newRetryAttempts} of ${MAX_RETRY_ATTEMPTS})`);
              fetchPriceData();
            }, 2000); // Retry after 2 seconds
          } else {
            // We've reached max retries, stop silently retrying and show the "No data found" message
            console.log(`Maximum retry attempts (${MAX_RETRY_ATTEMPTS}) reached. Showing "No data found" message.`);
            setSilentRetry(false);
            setLoading(false);
          }
        }
      } catch (err) {
        console.error('Error in price comparison component:', err);
        
        // Increment retry attempts
        const newRetryAttempts = retryAttempts + 1;
        setRetryAttempts(newRetryAttempts);
        
        // Only keep retrying silently if we haven't reached the max attempts
        if (newRetryAttempts < MAX_RETRY_ATTEMPTS) {
          // Don't show error to user
          setSilentRetry(true);
          
          setDebugInfo({
            error: err.message,
            stack: err.stack
          });
          
          // Setup immediate retry for error
          retryTimerRef.current = setTimeout(() => {
            console.log(`Retrying after error (attempt ${newRetryAttempts} of ${MAX_RETRY_ATTEMPTS})`);
            fetchPriceData();
          }, 3000); // Retry after 3 seconds
        } else {
          // We've reached max retries, stop silently retrying and show the "No data found" message
          console.log(`Maximum retry attempts (${MAX_RETRY_ATTEMPTS}) reached. Showing "No data found" message.`);
          setSilentRetry(false);
          setLoading(false);
        }
      } finally {
        if (!silentRetry) {
          setLoading(false);
        }
      }
    };
    
    // Clear any existing timers before setting new ones
    if (loadingTimerRef.current) {
      clearTimeout(loadingTimerRef.current);
    }
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
    }
    
    // Reset retry attempts when product ID changes
    setRetryAttempts(0);
    
    fetchPriceData();
    
    // Cleanup function
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: retryAttempts/silentRetry are read inside fetchPriceData closure
  }, [productId, csvLookupId, csvExactMatchOnly, prefetchedOffers]); // prefetchedOffers: when provided, skip fetch (DB-driven path)

  // Effect to track loading time
  useEffect(() => {
    if (loading) {
      // Reset loading duration when loading starts
      setLoadingDuration(0);
      
      // Create interval to track loading duration
      loadingTimerRef.current = setInterval(() => {
        setLoadingDuration(prev => prev + 1);
      }, 1000);
    } else {
      // Clear interval when loading stops
      if (loadingTimerRef.current) {
        clearInterval(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    }
    
    // Cleanup function
    return () => {
      if (loadingTimerRef.current) {
        clearInterval(loadingTimerRef.current);
      }
    };
  }, [loading]);

  const sortProductData = () => {
    if (!productData.length) return [];
    return sortVerifiedOffers(filterValidPricedOffers(productData), {
      isAmpedOutdoors,
    });
  };

  // Format number or price string with $ and thousands separator (e.g. $1,399.99)
  const formatCurrency = (numOrStr) => {
    if (numOrStr == null || numOrStr === '') return 'N/A';
    const num = typeof numOrStr === 'number' ? numOrStr : parseFloat(String(numOrStr).replace(/[$,]/g, ''));
    if (isNaN(num)) return 'N/A';
    return num.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatPrice = (priceStr) => {
    if (!priceStr) return 'N/A';
    const price = priceStr.trim();
    if (!price) return 'N/A';
    const num = parseFloat(price.replace(/[$,]/g, ''));
    if (isNaN(num)) return price.startsWith('$') ? price : `$${price}`;
    return formatCurrency(num);
  };

  // Apply 10% discount to Amped Outdoors price
  const getDiscountedAmpedPrice = (originalPrice) => {
    const cleanedPrice = originalPrice.replace(/[$,]/g, '');
    const price = parseFloat(cleanedPrice);
    if (isNaN(price)) return originalPrice;
    return (price * 0.9).toFixed(2);
  };

  // Get display price (with discount for Amped Outdoors if applicable)
  const getDisplayPrice = (item) => {
    if (isAmpedOutdoors && isAmpedOutdoorsRetailer(item.Source)) {
      const discountedPrice = getDiscountedAmpedPrice(item.Price);
      return formatCurrency(parseFloat(discountedPrice));
    }
    return formatPrice(item.Price);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    
    try {
      // Skip displaying FALLBACK text if present
      let cleanDateStr = dateStr;
      if (typeof dateStr === 'string' && dateStr.includes('FALLBACK')) {
        cleanDateStr = dateStr.replace('FALLBACK ', '');
      }
      
      const dev = process.env.NODE_ENV === 'development';
      if (dev) console.log('Date string before parsing:', cleanDateStr);

      // Try to parse the date
      const date = new Date(cleanDateStr);
      
      // If date is invalid, return the original string 
      if (isNaN(date.getTime())) {
        if (dev) console.log('Invalid date detected:', cleanDateStr);
        // Try to extract date parts if it's a custom format
        return dateStr;
      }
      
      if (dev) console.log('Successfully parsed date:', date.toString());
      
      // Format the date properly with full date and time
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).format(date);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error formatting date:', e);
      }
      // Return original string on error
      return dateStr;
    }
  };



  const getRetailerDisplayName = (item) => item?.sourceDisplay || item?.Source || 'Unknown';

  const getProductTitle = () => {
    if (isPrefetchedDbOffers && pricingProductName) return pricingProductName;
    if (productDetails) return productDetails.name;
    if (!productData.length) return 'Product Price Comparison';
    return productData[0].Product || 'Product Price Comparison';
  };

  // Calculate savings compared to MSRP (if available) or highest price
  const calculateSavings = () => {
    const sortedData = sortProductData();
    if (sortedData.length === 0) return null;
    
    let lowestPrice = sortedData[0]?.numeric_price;
    if (typeof lowestPrice !== 'number' || !Number.isFinite(lowestPrice) || lowestPrice <= 0) {
      return null;
    }
    
    // Apply Amped Outdoors discount to lowest price if applicable
    if (isAmpedOutdoors && isAmpedOutdoorsRetailer(sortedData[0].Source)) {
      lowestPrice = lowestPrice * 0.9;
    }
    
    if (!Number.isFinite(lowestPrice) || lowestPrice <= 0) return null;
    
    // Use MSRP if available, otherwise fall back to highest price
    let referencePrice = msrp;
    if (!referencePrice && sortedData.length >= 2) {
      referencePrice = sortedData[sortedData.length - 1]?.numeric_price;
    }
    
    if (typeof referencePrice !== 'number' || !Number.isFinite(referencePrice) || referencePrice <= 0) return null;
    
    const savings = referencePrice - lowestPrice;
    // Return null if there's no actual savings (prices are the same or lower than reference)
    if (savings <= 0) return null;
    
    const savingsPercent = Math.round((savings / referencePrice) * 100);
    
    return {
      amount: savings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      percent: savingsPercent,
      referenceType: 'MSRP',
      referencePrice: referencePrice
    };
  }

  // Function to handle copy link to clipboard
  const handleCopyLink = () => {
    const currentUrl = window.location.href;
    navigator.clipboard.writeText(currentUrl)
      .then(() => {
        setShowCopyToast(true);
        setTimeout(() => setShowCopyToast(false), 3000); // Hide toast after 3 seconds
      })
      .catch(err => {
        console.error('Could not copy text: ', err);
      });
  };

  // Function to handle retry button click
  const handleRetryClick = () => {
    // Reset retry attempts
    setRetryAttempts(0);
    // Start loading again
    setSilentRetry(false);
    setLoading(true);
  };

  // Function to toggle QR modal
  const toggleQrModal = () => {
    setShowQRCode(!showQRCode);
  };

  // QR code modal component
  const QrModal = () => {
    if (!showQRCode) return null;
    
    return (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}
        onClick={toggleQrModal}
      >
        <div 
          className="qr-modal"
          style={{
            background: theme.isDarkMode ? '#1c1c1c' : '#ffffff',
            borderRadius: '8px',
            padding: '16px',
            maxWidth: '300px',
            textAlign: 'center',
            position: 'relative',
            boxShadow: '0 0 12px rgba(0,0,0,0.4)',
          }}
          onClick={e => e.stopPropagation()}
        >
          <button 
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'none',
              border: 'none',
              fontSize: '16px',
              cursor: 'pointer',
              color: theme.colors.text,
            }}
            onClick={toggleQrModal}
            aria-label="Close QR code"
          >
            ❌
          </button>
          <h3 style={{ marginTop: '10px', marginBottom: '15px', color: theme.colors.text }}>
            Scan QR Code
          </h3>
          <div style={{ marginBottom: '15px' }}>
            <QRCodeSVG
              value={window.location.href}
              size={200}
              level={"M"}
              includeMargin={true}
              bgColor={theme.isDarkMode ? '#1c1c1c' : '#ffffff'}
              fgColor={theme.isDarkMode ? '#ffffff' : '#000000'}
            />
          </div>
          <p style={{ fontSize: '13px', color: theme.colors.textSecondary }}>
            Scan with your phone camera to visit this page
          </p>
        </div>
      </div>
    );
  };

  // Add debug display component
  const DebugDisplay = () => {
    if (!showDebug) return null;
    
    return (
      <div style={{
        margin: '20px 0',
        padding: '15px',
        background: '#000',
        color: '#0f0',
        fontFamily: 'monospace',
        fontSize: '12px',
        borderRadius: '4px',
        whiteSpace: 'pre-wrap',
        overflowX: 'auto'
      }}>
        <h3 style={{ color: '#0f0', margin: '0 0 10px 0' }}>Debug Information</h3>
        <div>
          <p style={{ color: '#0f0', margin: '0 0 10px 0' }}>
            Window width: {windowWidth}px | isMobile: {isMobile.toString()} | isDesktop: {isDesktop.toString()}
          </p>
          <button 
            onClick={() => {
              // Force refresh pricing data through the normal API path
              fetchProductPrices(productId).then(result => {
                setProductData(result.data || []);
                setDebugInfo({
                  ...debugInfo,
                  refreshResult: {
                    timestamp: new Date().toISOString(),
                    source: result.source,
                    itemCount: result.data?.length || 0
                  }
                });
              }).catch(err => {
                setDebugInfo({
                  ...debugInfo,
                  refreshError: err.message
                });
              });
            }}
            style={{
              background: '#333',
              color: '#0f0',
              border: '1px solid #0f0',
              padding: '5px 10px',
              marginRight: '10px',
              cursor: 'pointer'
            }}
          >
            Force Refresh
          </button>
        </div>
        <pre style={{ margin: '10px 0 0 0' }}>
          {JSON.stringify(debugInfo, null, 2)}
        </pre>
        <pre style={{ margin: '10px 0 0 0' }}>
          Current data items: {productData.length}
          {productData.length > 0 && (
            <>
              <br />First item: {JSON.stringify(productData[0], null, 2)}
              <br /><br />Date format details:
              <br />Raw date string: {productData[0]["Date Time Stamp"]}
              <br />Formatted date: {formatDate(productData[0]["Date Time Stamp"])}
              <br />Is valid date: {!isNaN(new Date(productData[0]["Date Time Stamp"]).getTime()) ? 'Yes' : 'No'}
            </>
          )}
        </pre>
      </div>
    );
  };

  const heroTitle = heroProductName || getProductTitle();
  const sortedOffers = sortProductData();
  const priceOptions = { isAmpedOutdoors };
  const tiedBestOffers = getBestVerifiedOffers(sortedOffers, priceOptions);
  const heroLowestOffer = getBestVerifiedOffer(sortedOffers, priceOptions);
  const heroLowestComparablePrice = heroLowestOffer
    ? getComparableOfferPrice(heroLowestOffer, priceOptions)
    : null;
  const isUniqueCurrentLowest = tiedBestOffers.length === 1;

  const handleDbPdpAffiliateClick = (offer) => {
    trackDbPdpOfferClick(offer, {
      productSlug,
      isPrefetchedDbOffers,
      priceOptions,
    });
  };

  const renderOfferCta = (item, isLowestRow, index) => {
    const link = getOfferLink(item);
    const ctaStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '2px',
      padding: isMobile ? '9px 12px' : '10px 18px',
      background: isLowestRow
        ? `linear-gradient(90deg, ${PRICE_GREEN}, ${PRICE_GREEN_DARK})`
        : theme.colors.accent,
      color: 'white',
      textDecoration: 'none',
      borderRadius: '8px',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      textAlign: 'center',
      minWidth: isMobile ? 'auto' : '130px',
      boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
      transition: 'all 0.2s ease',
      flexShrink: 0,
      whiteSpace: 'nowrap',
    };

    const retailerLabel = item?.sourceDisplay || item?.Source || 'retailer';
    const ctaLabel = (
      <>
        {embeddedInProductPage ? `Check price at ${retailerLabel}` : 'Check Availability'}
        <span className="cta-chevron" aria-hidden="true">
          {' ›'}
        </span>
      </>
    );

    if (!link) {
      return (
        <span className="offer-cta offer-cta--disabled" style={{ ...ctaStyle, opacity: 0.6, cursor: 'default' }}>
          {ctaLabel}
        </span>
      );
    }

    return (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="offer-cta"
        style={ctaStyle}
        data-testid={`pdp-offer-cta-${index}`}
        onClick={() => handleDbPdpAffiliateClick(item)}
      >
        {ctaLabel}
      </a>
    );
  };

  usePriceSectionView(
    priceSectionRef,
    {
      pageSource: AFFILIATE_PAGE_SOURCE.PRODUCT_PDP,
      productSlug,
      retailerCount: sortedOffers.length,
      hasCurrentVerifiedPrice: Boolean(heroLowestOffer),
      freshnessState: heroLowestOffer ? 'CURRENT' : 'NONE',
      placement: 'pdp_offers',
    },
    Boolean(embeddedInProductPage && productSlug && sortedOffers.length)
  );

  return (
    <div className={`main-container ${isDesktop ? 'desktop' : ''} ${embeddedInProductPage ? 'embedded-pdp' : ''}`} style={{ 
      color: theme.colors.text,
      background: theme.colors.background,
    }}>
      {/* Debug panel */}
      <DebugDisplay />
      
      {/* QR code modal */}
      <QrModal />

      {embeddedInProductPage && (
        <ProductComparisonHero
          productName={heroTitle}
          productImage={heroProductImage}
          fallbackImage={heroFallbackImage}
          msrp={msrp}
          lowestOffer={heroLowestOffer}
          lowestPriceLabel={heroLowestOffer ? getDisplayPrice(heroLowestOffer) : null}
          lowestComparablePrice={heroLowestComparablePrice}
          tiedBestOffers={tiedBestOffers}
          loading={loading || silentRetry}
          theme={theme}
          isMobile={isMobile}
          imageOverlay={heroImageOverlay}
          onAffiliateClick={handleDbPdpAffiliateClick}
        />
      )}
      
      {!embeddedInProductPage && (
        <div className={`section ${isDesktop ? 'desktop' : ''}`} style={{
          textAlign: 'center',
          background: theme.isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)',
          borderRadius: '10px',
          boxShadow: theme.isDarkMode ? 'inset 0 0 10px rgba(0,0,0,0.2)' : 'inset 0 0 10px rgba(0,0,0,0.05)',
        }}>
          <p style={{
            fontSize: isMobile ? '14px' : '16px',
            color: theme.colors.textSecondary,
            margin: '0 0 10px 0'
          }}>
            🎯 Current Verified Price Comparison Tool
            <br />
            <span style={{ 
              fontSize: isMobile ? '12px' : '14px', 
              fontWeight: 'normal',
              opacity: 0.9
            }}>
              See current verified retailer prices from trusted stores, updated daily.
            </span>
          </p>
          
          {!loading && !error && (() => {
            const savings = calculateSavings();
            
            if (savings) {
              return (
                <div style={{
                  display: 'inline-block',
                  background: theme.isDarkMode ? 'rgba(230, 126, 34, 0.2)' : 'rgba(230, 126, 34, 0.15)',
                  padding: isMobile ? '8px 16px' : '10px 20px',
                  borderRadius: '12px',
                  margin: '8px 0 15px 0',
                  border: '2px solid rgba(230, 126, 34, 0.4)',
                  boxShadow: theme.isDarkMode ? '0 4px 15px rgba(230, 126, 34, 0.2)' : '0 4px 15px rgba(230, 126, 34, 0.1)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(230, 126, 34, 0.1), transparent)',
                    animation: 'shimmer 3s ease-in-out infinite'
                  }}></div>
                  
                  <span style={{
                    fontSize: isMobile ? '15px' : '17px',
                    fontWeight: '700',
                    color: '#e67e22',
                    textShadow: theme.isDarkMode ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    💰 Save up to ${savings.amount} vs {savings.referenceType === 'MSRP' ? 'MSRP' : savings.referenceType.replace(/\$/g, '')}
                  </span>
                  
                  <style>{`
                    @keyframes shimmer {
                      0% { left: -100%; }
                      50% { left: 100%; }
                      100% { left: 100%; }
                    }
                  `}</style>
                </div>
              );
            }
            
            return null;
          })()}
        </div>
      )}

      {loading || silentRetry ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px 20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px'
        }}>
          {/* Animated Loader - Pulsing Dots with Spinner */}
          <div className="loading-dots" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '15px'
          }}>
            <span style={{
              fontSize: '24px',
              fontWeight: '500',
              color: theme.colors.text,
              marginRight: '10px',
              opacity: '0.7'
            }}>
              Loading price data
            </span>
            
            {/* Spinner Animation */}
            <div className="spinner" style={{
              width: '20px',
              height: '20px',
              border: `2px solid ${theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
              borderTop: `2px solid ${theme.isDarkMode ? '#ffffff' : '#333333'}`,
              borderRadius: '50%',
              animation: 'spin 1.5s linear infinite'
            }}></div>
          </div>
          
          {/* Reassurance Message - Appears after 3 seconds */}
          <div className={`reassurance-message ${loadingDuration >= 3 ? 'show' : ''}`} style={{
            opacity: loadingDuration >= 3 ? 1 : 0,
            transition: 'opacity 0.5s ease-in',
            fontSize: '14px',
            color: theme.isDarkMode ? '#aaa' : '#777',
            maxWidth: '300px',
            margin: '0 auto'
          }}>
            Still loading? Some retailers take a few extra seconds to send live pricing.
          </div>
          
          {/* Fishing Animation - Appears after 8 seconds */}
          {loadingDuration >= 8 && (
            <div className="fishing-animation" style={{ 
              marginTop: '25px',
              height: '50px',
              position: 'relative'
            }}>
              <div className="fishing-bobber"></div>
            </div>
          )}
          
          {/* CSS for Animations */}
          <style>{`
            /* Spinner Animation */
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            
            /* Pulsing Dots Animation */
            .dot {
              display: inline-block;
              width: 8px;
              height: 8px;
              border-radius: 50%;
              margin: 0 4px;
              background-color: ${theme.isDarkMode ? '#fff' : '#333'};
              opacity: 0.3;
            }
            
            .dot-1 {
              animation: pulse 1.5s infinite;
            }
            
            .dot-2 {
              animation: pulse 1.5s infinite 0.3s;
            }
            
            .dot-3 {
              animation: pulse 1.5s infinite 0.6s;
            }
            
            @keyframes pulse {
              0% { transform: scale(1); }
              50% { transform: scale(1.02); }
              100% { transform: scale(1); }
            }
            
            /* Fishing Bobber Animation */
            .fishing-bobber {
              position: absolute;
              width: 14px;
              height: 14px;
              border-radius: 50%;
              background: linear-gradient(to bottom, #ff3333 50%, #ffffff 50%);
              box-shadow: 0 1px 3px rgba(0,0,0,0.3);
              left: 50%;
              transform: translateX(-50%);
              animation: bobbing 2s ease-in-out infinite;
            }
            
            @keyframes bobbing {
              0%, 100% { top: 0; }
              50% { top: 15px; }
            }
            
            /* Fade in/out for reassurance message */
            .reassurance-message {
              opacity: 0;
              transition: opacity 0.8s ease-in;
            }
            
            .reassurance-message.show {
              opacity: 1;
            }
          `}</style>
        </div>
      ) : productData.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px 20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px'
        }}>
          {loading ? (
            <>
              {/* Animated Loader - Pulsing Dots */}
              <div className="loading-dots" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '15px'
              }}>
                <span style={{
                  fontSize: '24px',
                  fontWeight: '500',
                  color: theme.colors.text,
                  marginRight: '10px'
                }}>
                  Loading price data
                </span>
                <div className="dots-container" style={{ display: 'inline-flex' }}>
                  <div className="dot dot-1"></div>
                  <div className="dot dot-2"></div>
                  <div className="dot dot-3"></div>
                </div>
              </div>
              
              {/* Fishing Animation shown immediately as reassurance */}
              <div className="fishing-animation" style={{ 
                marginTop: '25px',
                height: '50px',
                position: 'relative'
              }}>
                <div className="fishing-bobber"></div>
              </div>
              
              <div style={{
                fontSize: '14px',
                color: theme.isDarkMode ? '#aaa' : '#777',
                maxWidth: '300px',
                margin: '20px auto 0',
                opacity: 1,
                transition: 'opacity 0.5s ease-in'
              }}>
                Some retailers take a bit longer to connect. We'll keep trying until we get the latest prices!
              </div>
            </>
          ) : (
            <>
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '20px', opacity: 0.7 }}>
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke={theme.isDarkMode ? "#bbbbbb" : "#777777"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 8V12" stroke={theme.isDarkMode ? "#bbbbbb" : "#777777"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 16H12.01" stroke={theme.isDarkMode ? "#bbbbbb" : "#777777"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <h3 style={{ 
                fontSize: '20px', 
                color: theme.colors.text, 
                marginBottom: '10px',
                fontWeight: '500'
              }}>
                No price data found
              </h3>
              <p style={{
                fontSize: '14px',
                color: theme.isDarkMode ? '#aaa' : '#777',
                maxWidth: '300px',
                margin: '0 auto 20px',
                textAlign: 'center'
              }}>
                We couldn't find any matching price data for this product at the moment.
              </p>
              <button 
                onClick={handleRetryClick}
                style={{
                  background: theme.colors.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                  <path d="M1 4V10H7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M23 20V14H17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M20.49 9.00001C19.9828 7.56678 19.1209 6.2743 17.9845 5.27439C16.8482 4.27448 15.4745 3.60243 14 3.32001C12.5255 3.03759 11.0097 3.15868 9.6007 3.67143C8.19171 4.18418 6.93574 5.07117 5.96999 6.23001L1 10M23 14L18.03 17.77C17.0643 18.929 15.8083 19.816 14.3993 20.3287C12.9903 20.8415 11.4745 20.9626 10 20.68C8.52552 20.3975 7.15178 19.7255 6.01545 18.7256C4.87912 17.7257 4.01718 16.4332 3.51 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Retry
              </button>
            </>
          )}
        </div>
      ) : (
        <>
          {embeddedInProductPage ? (
            <h2 className="pdp-offers-heading" data-testid="pdp-offers-heading">
              Current retailer prices
            </h2>
          ) : null}
          <div ref={priceSectionRef} className={`price-card-container ${isDesktop ? 'desktop' : ''} ${embeddedInProductPage ? 'embedded-pdp-offers' : ''}`} style={{
            borderRadius: '12px',
            overflow: 'hidden',
            border: `1px solid ${theme.colors.border}`,
            boxShadow: theme.isDarkMode ? 
              '0 3px 8px rgba(0,0,0,0.2)' : 
              '0 3px 8px rgba(0,0,0,0.05)',
          }}>
            {sortedOffers.map((item, index) => {
              const isCurrentLowestRow = tiedBestOffers.includes(item);
              const highlightLowest = isUniqueCurrentLowest && isCurrentLowestRow;
              const presentation = embeddedInProductPage
                ? getOfferListPresentation(item, priceOptions)
                : null;
              const freshness = presentation?.meta || null;
              const sale = presentation && !presentation.isCurrent ? null : getOfferSavings(item);

              return (
              <div 
                key={item.offerKey || `${item.Source}-${index}`} 
                className={highlightLowest ? 'offer-row offer-row--lowest' : 'offer-row'}
                data-testid={embeddedInProductPage ? `pdp-offer-row-${index}` : undefined}
                data-offer-state={presentation?.state || undefined}
                style={{
                  borderBottom: index < sortedOffers.length - 1 ? `1px solid ${theme.colors.border}` : 'none',
                  background: highlightLowest ? 
                    (theme.isDarkMode ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.06)') : 
                    (theme.isDarkMode ? '#1a1a1a' : theme.colors.surface),
                }}
              >
                <div className="offer-row__info">
                  <div className="offer-row__retailer">
                    <RetailerLogo name={item.logoRetailer || item.Source} className="offer-row__logo" />
                    <div className="offer-row__retailer-text">
                      <h3 
                        className="offer-row__name"
                        style={{ color: theme.isDarkMode ? '#fff' : '#333' }}
                      >
                        {getRetailerDisplayName(item)}
                      </h3>
                      {item.isMergedRelatedOffer && (
                        <p
                          className="offer-row__variant"
                          style={{ color: theme.colors.textSecondary }}
                        >
                          Ice Fishing IF Version
                        </p>
                      )}
                      {item.variant_label && <p className="offer-row__variant">{item.variant_label}</p>}
                      {embeddedInProductPage ? (
                        (freshness || presentation?.disclaimer) ? (
                          <p
                            className="offer-row__updated"
                            style={{ color: theme.colors.textSecondary }}
                            data-testid={`pdp-offer-freshness-${index}`}
                          >
                            {freshness}
                            {presentation?.disclaimer ? (
                              <>
                                {freshness ? ' · ' : null}
                                <span data-testid={`pdp-offer-stale-note-${index}`}>
                                  {presentation.disclaimer}
                                </span>
                              </>
                            ) : null}
                          </p>
                        ) : null
                      ) : (
                        <p
                          className="offer-row__updated"
                          style={{ color: theme.colors.textSecondary }}
                        >
                          <span aria-hidden="true">⟳</span>
                          Updated: {formatDate(item["Date Time Stamp"])}
                        </p>
                      )}
                      {Array.isArray(item.variantInfo) && item.variantInfo.length > 0 && (
                        <details className="offer-row__variant-details">
                          <summary style={{ color: theme.colors.text }}>
                            What&apos;s the difference?
                          </summary>
                          <ul>
                            {item.variantInfo.map((line) => (
                              <li key={line}>{line}</li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="offer-row__deal">
                  <div className="offer-row__price-block">
                    {sale?.onSale && <s className="pdp-v1-sale-reference">Regular {formatHeroCurrency(sale.referencePrice)}</s>}
                    <div className="offer-row__price-line">
                      <div 
                        className="offer-row__price"
                        style={{ color: highlightLowest ? PRICE_GREEN : (theme.isDarkMode ? '#fff' : '#333') }}
                      >
                        <span data-testid={embeddedInProductPage ? `pdp-offer-price-${index}` : undefined}>
                          {getDisplayPrice(item)}
                        </span>
                      </div>
                      {highlightLowest && (
                        <span className="offer-row__lowest-badge">
                          Lowest Price
                        </span>
                      )}
                    </div>
                    
                    {isAmpedOutdoors && isAmpedOutdoorsRetailer(item.Source) && (
                      <div className="offer-row__amped-note">
                        <div style={{
                          fontSize: '12px',
                          color: theme.isDarkMode ? '#999' : '#666',
                          textDecoration: 'line-through'
                        }}>
                          Original: {formatPrice(item.Price)}
                        </div>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          color: theme.isDarkMode ? '#FF9800' : '#F57C00',
                          background: theme.isDarkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(255, 152, 0, 0.05)',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          border: `1px solid ${theme.isDarkMode ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.2)'}`,
                          whiteSpace: 'nowrap'
                        }}>
                          10% OFF - Add to cart to see discount
                        </div>
                      </div>
                    )}

                    {msrp && (() => {
                      const currentPrice = item?.numeric_price;
                      const rowSavings = computeMsrpSavings(msrp, currentPrice);
                      if (rowSavings == null) return null;
                      return (
                        <div className="offer-row__savings">
                          Save ${rowSavings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} off MSRP
                        </div>
                      );
                    })()}
                    {sale?.onSale && <OfferSavingsBadge savings={sale}/>}
                  </div>
                  
                  {renderOfferCta(item, highlightLowest, index)}
                </div>
              </div>
            );
            })}
          </div>
          
          <AmazonPriceNotice offers={productData} />
          {/* Mini affiliate disclosure - Moved above social sharing */}
          {embeddedInProductPage ? (
            <p className="pdp-price-transparency" data-testid="pdp-price-transparency">
              Retailer prices are current verified store offers and can change. MSRP is a
              reference price only and is not a live purchase price.
            </p>
          ) : null}
          <div className={`affiliate-note ${isDesktop ? 'desktop' : ''}`} style={{
            fontSize: isMobile ? '11px' : '12px',
            lineHeight: '1.4',
            color: '#ccc',
            textAlign: 'center',
            padding: '5px',
            marginBottom: '20px'
          }}>
            <p style={{ margin: '0' }}>
              ℹ️ Note: This page contains affiliate links. I may earn a small commission if you click and buy, at no extra cost to you.
            </p>
            
            {/* Full affiliate disclosure moved here */}
            <details style={{ 
              fontSize: isMobile ? '10px' : '11px',
              marginTop: '12px'
            }}>
              <summary style={{ cursor: 'pointer', marginBottom: '0' }}><strong>Learn how this page is supported</strong></summary>
              <div style={{ padding: '5px 0 0 15px', textAlign: 'left' }}>
                <p style={{ margin: '5px 0 0 0' }}>
                  Some links on this page are affiliate links, meaning I may earn a small commission if you click through and make a purchase, at no extra cost to you. 
                  <strong> As an Amazon Associate, I earn from qualifying purchases.</strong> This helps support the content I create and allows me to keep this page updated with the latest prices from trusted retailers.
                </p>
              </div>
            </details>
          </div>
          
          {/* Social Sharing Bar - now shown on all screen sizes */}
          <div className="social-share-bar" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: '20px',
            marginBottom: '20px',
            padding: '15px',
            borderRadius: '8px',
            background: theme.isDarkMode ? '#1c1c1c' : '#f5f5f5',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            position: 'relative'
          }}>
            <p style={{
              margin: '0 0 12px 0',
              fontSize: '14px',
              fontWeight: '600',
              color: theme.isDarkMode ? '#e0e0e0' : '#333'
            }}>
              Share or Save This Deal:
            </p>
            
            <div className="share-buttons-container" style={{
              display: 'flex',
              justifyContent: 'center',
              gap: isMobile ? '8px' : '16px',
              width: '100%'
            }}>
              {/* Copy Link Button */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: isMobile ? '55px' : '70px',
                gap: '0'
              }}>
                <button
                  onClick={handleCopyLink}
                  aria-label="Copy link"
                  className="share-button"
                  style={{
                    backgroundColor: "#2c2c2c",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "44px",
                    height: "44px",
                    padding: 0,
                    margin: 0,
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "8px"
                  }}
                >
                  <FaLink size={18} />
                  {showCopyToast && (
                    <div className="toast-notification" style={{
                      position: "absolute",
                      bottom: "-40px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      backgroundColor: "rgba(0,0,0,0.8)",
                      color: "#fff",
                      padding: "8px 12px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      zIndex: 10,
                    }}>
                      Link copied!
                    </div>
                  )}
                </button>
                <span style={{
                  fontSize: "11px",
                  marginTop: "0",
                  padding: 0,
                  margin: 0,
                  lineHeight: "1",
                  display: "block",
                  position: "relative",
                  top: "-4px",
                  textAlign: "center"
                }}>
                  Copy
                </span>
              </div>
              
              {/* QR Code Button */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: isMobile ? '55px' : '70px',
                gap: '0'
              }}>
                <button
                  onClick={() => setShowQRCode(!showQRCode)}
                  aria-label="Show QR Code"
                  className="share-button"
                  style={{
                    backgroundColor: "#2c2c2c",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "44px",
                    height: "44px",
                    padding: 0,
                    margin: 0,
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "8px"
                  }}
                >
                  <FaQrcode size={18} />
                </button>
                <span style={{
                  fontSize: "11px",
                  marginTop: "0",
                  padding: 0,
                  margin: 0,
                  lineHeight: "1",
                  display: "block",
                  position: "relative",
                  top: "-4px",
                  textAlign: "center"
                }}>
                  QR Code
                </span>
              </div>
              
              {/* SMS Share Button */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: isMobile ? '55px' : '70px',
                gap: '0'
              }}>
                <a
                  href={`sms:?body=${encodeURIComponent(
                    `Check out this deal: ${window.location.href}`
                  )}`}
                  aria-label="Share via SMS"
                  className="share-button"
                  style={{
                    backgroundColor: "#2c2c2c",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "44px",
                    height: "44px",
                    padding: 0,
                    margin: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textDecoration: "none",
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                    marginBottom: "8px"
                  }}
                >
                  <FaSms size={18} />
                </a>
                <span style={{
                  fontSize: "11px",
                  marginTop: "0",
                  padding: 0,
                  margin: 0,
                  lineHeight: "1",
                  display: "block",
                  position: "relative",
                  top: "-4px",
                  textAlign: "center"
                }}>
                  Send Text
                </span>
              </div>
              
              {/* Facebook Share Button */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: isMobile ? '55px' : '70px',
                gap: '0'
              }}>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                    window.location.href
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on Facebook"
                  className="share-button"
                  style={{
                    backgroundColor: "#3b5998",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "44px",
                    height: "44px",
                    padding: 0,
                    margin: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textDecoration: "none",
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                    marginBottom: "8px"
                  }}
                >
                  <FaFacebookF size={18} />
                </a>
                <span style={{
                  fontSize: "11px",
                  marginTop: "0",
                  padding: 0,
                  margin: 0,
                  lineHeight: "1",
                  display: "block",
                  position: "relative",
                  top: "-4px",
                  textAlign: "center"
                }}>
                  Facebook
                </span>
              </div>
              
              {/* Twitter Share Button */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: isMobile ? '55px' : '70px',
                gap: '0'
              }}>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(
                    window.location.href
                  )}&text=${encodeURIComponent(
                    `Check out this deal on ${(isPrefetchedDbOffers && pricingProductName) ? pricingProductName : (productDetails?.name || "this product")}!`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on Twitter"
                  className="share-button"
                  style={{
                    backgroundColor: "#1da1f2",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "44px",
                    height: "44px",
                    padding: 0,
                    margin: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textDecoration: "none",
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                    marginBottom: "8px"
                  }}
                >
                  <FaTwitter size={18} />
                </a>
                <span style={{
                  fontSize: "11px",
                  marginTop: "0",
                  padding: 0,
                  margin: 0,
                  lineHeight: "1",
                  display: "block",
                  position: "relative",
                  top: "-4px",
                  textAlign: "center"
                }}>
                  X
                </span>
              </div>
            </div>
            
            {/* Tip message moved inside social share bar */}
            <div style={{
              textAlign: 'center',
              color: '#aaa',
              fontSize: '13px',
              margin: '15px auto 0',
              opacity: 0.8,
              maxWidth: '80%'
            }}>
              💡 Tip: Bookmark this page and check back tomorrow. Prices are updated daily.
            </div>
          </div>
          
          {/* SEO-friendly content */}
          <div className={`section ${isDesktop ? 'desktop' : ''}`} style={{
            padding: '15px',
            borderTop: `1px solid ${theme.isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
            fontSize: isMobile ? '13px' : '14px',
            lineHeight: '1.6',
            color: theme.colors.textSecondary,
            textAlign: 'justify',
          }}>
            <p>
              The {getProductTitle()} is a high-quality fishing electronics product. 
              Compare current pricing from trusted retailers like Amazon, Scheels, and more. 
              Updated daily so you get the best deal without the research.
            </p>
          </div>
        </>
      )}
      
      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.02); }
          100% { transform: scale(1); }
        }
        
        div[title]:hover .price-tooltip {
          opacity: 1;
        }
        
        @keyframes highlightFeedback {
          0% { box-shadow: 0 0 0 0 rgba(41, 121, 255, 0.2); }
          50% { box-shadow: 0 0 0 10px rgba(41, 121, 255, 0.4); }
          100% { box-shadow: 0 0 0 0 rgba(41, 121, 255, 0.2); }
        }
        
        .highlight-feedback {
          animation: highlightFeedback 1.5s ease-in-out 3;
          border: 2px solid #2979ff !important;
          transition: all 0.3s ease;
        }
        
        .share-button:hover {
          background-color: rgba(255, 255, 255, 0.15) !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          transform: translateY(-1px);
        }
      `}</style>
    </div>
  );
};

export default SimplePriceComparison;
